/**
 * `updateLiveBucket` — owns realtime patching of sparse event-time buckets
 * on the time series cached by `useGetMarketChart`, from BONDING-CURVE trade
 * events (`morfi.market.${pubkey}.user.*.trade.v0`, proto `MarketTrade`).
 *
 * On every sequenced event the helper patches its event-time bucket or inserts
 * an occupied bucket in time order. A delayed trade may update a closed bucket;
 * the chart does not re-fetch after its initial snapshot.
 *
 * Value source — picked off the trade payload by `args.property` and stored
 * as the RAW integer string (same scale as the REST buckets; the frontend
 * divides once per row):
 *   - `price`                   → `postOutcomePriceD9` — the post-reserve
 *                                  spot, matching the price candles re-rooted
 *                                  over trade post-spot. `executionPriceD9`
 *                                  presence is the producer-version gate:
 *                                  legacy producers omit it and put the
 *                                  execution average in `postOutcomePriceD9`;
 *                                  those events are rejected — never an
 *                                  execution fallback for spot.
 *   - `market_cap`              → effective mcap from `postBcQuoteReservesD6`
 *                                  (pool^2 / virtualQuote - virtualQuote, raw D6 integer)
 *   - `reserves`                → `postBcQuoteReservesD6` (raw D6 integer)
 *
 * Drop the event when `payload.outcomeIdx !== args.outcomeIndex`.
 *
 * The shared bucket-patch / rollover / structural-sharing logic lives in
 * `live-bucket-core.ts` and is reused by the AMM helper. Returns the EXACT
 * `prev` reference on every no-op.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import type { PagedData } from '../types/PagedData.js';
import { effectiveMcapD6 } from './effective-mcap.js';
import {
  applyBucketPatch,
  type ChartClock,
  type ChartQueryArgs,
  type ChartRow,
  readEventOutcomeIdx,
  readEventTsSeconds,
  readIntegerString,
  readOutcomeIndexArg,
  SYSTEM_CHART_CLOCK,
  throwChartRealtimeValueUnavailable,
} from './live-bucket-core.js';

function parseVirtualQuote(raw: string): bigint | null {
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  const value = BigInt(trimmed);
  return value > 0n ? value : null;
}

function readVirtualQuote<Row extends ChartRow>(prev: PagedData<Row>): bigint | null {
  return prev.virtualQuote === undefined ? null : parseVirtualQuote(prev.virtualQuote);
}

/**
 * Apply one decoded `MarketTrade` event to a `useGetMarketChart`
 * cached envelope. Patches or inserts the event-time bucket, or bails.
 * Returns `prev` verbatim on no-op.
 */
export function updateLiveBucket<Row extends ChartRow>(
  prev: PagedData<Row>,
  raw: Record<string, unknown>,
  args: ChartQueryArgs,
  clock: ChartClock = SYSTEM_CHART_CLOCK,
): PagedData<Row> {
  const outcomeIndex = readOutcomeIndexArg(args.outcomeIndex);
  if (outcomeIndex === null) {
    throwChartRealtimeValueUnavailable('bc_outcome_index_arg');
  }

  const { property } = args;

  const eventOutcomeIdx = readEventOutcomeIdx(raw);
  if (eventOutcomeIdx === null) {
    throwChartRealtimeValueUnavailable('bc_event_outcome_index');
  }
  if (eventOutcomeIdx !== outcomeIndex) return prev;

  // Resolve the RAW value off the event payload, in the SAME scale the REST
  // buckets use (D9 price / D6 reserves). It is stored verbatim —
  // the frontend applies the per-property scale converter once across every
  // row, so pre-dividing here would divide twice and collapse the live bucket
  // to ~0 (zeroing the y-axis range).
  let valueRaw: string | null = null;
  if (property === 'price') {
    // Producer-version gate: only producers that send `executionPriceD9`
    // are known to publish the post-trade spot in `postOutcomePriceD9`
    // (trading.proto defines the field as spot; older payloads are skipped
    // rather than trusted).
    const executionPriceD9 = readIntegerString(raw, 'executionPriceD9');
    if (executionPriceD9 === null || BigInt(executionPriceD9) <= 0n) {
      throwChartRealtimeValueUnavailable('bc_producer_version');
    }
    valueRaw = readIntegerString(raw, 'postOutcomePriceD9');
    if (valueRaw === null || BigInt(valueRaw) <= 0n) {
      throwChartRealtimeValueUnavailable('bc_post_spot_price');
    }
  } else if (property === 'market_cap') {
    const poolD6 = readIntegerString(raw, 'postBcQuoteReservesD6');
    const virtualD6 = readVirtualQuote(prev);
    if (poolD6 === null || BigInt(poolD6) <= 0n || virtualD6 === null) {
      throwChartRealtimeValueUnavailable('bc_market_cap_context');
    }
    const mcap = effectiveMcapD6(BigInt(poolD6), virtualD6);
    if (mcap === null) throwChartRealtimeValueUnavailable('bc_market_cap_domain');
    valueRaw = mcap.toString();
  } else if (property === 'reserves') {
    valueRaw = readIntegerString(raw, 'postBcQuoteReservesD6');
    if (valueRaw === null || BigInt(valueRaw) <= 0n) {
      throwChartRealtimeValueUnavailable('bc_post_reserves');
    }
  }
  if (valueRaw === null) {
    throwChartRealtimeValueUnavailable('bc_property');
  }

  const eventTs = readEventTsSeconds(raw);
  if (eventTs === null) {
    throwChartRealtimeValueUnavailable('bc_timestamp');
  }

  return applyBucketPatch(prev, args, valueRaw, eventTs, clock);
}
